import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/**
 * A CSV column reference: one header, or ordered candidates from which the parser
 * picks the first present non-empty value (tolerates multiple export shapes).
 */
export type ColumnRef = string | string[];

export interface ImportMapping {
  provider: string;
  shopifyCustomerId?: ColumnRef;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  tags?: string;
  acceptsEmailMarketing?: string;
  emailMarketingTrueValue?: string;
  emailMarketingUnsubscribedValue?: string;
  emailMarketingSuppressed?: string;
  // Opt-out held in a column separate from subscribe status (e.g. Attentive
  // "... Opted Out"); when it matches, unsubscribe wins over the status column.
  emailMarketingOptedOut?: string;
  emailMarketingOptedOutTrueValue?: string;
  acceptsSmsMarketing?: string;
  smsMarketingTrueValue?: string;
  smsMarketingUnsubscribedValue?: string;
  smsMarketingOptedOut?: string;
  smsMarketingOptedOutTrueValue?: string;
  address?: {
    street1?: string;
    street2?: string;
    city?: string;
    state?: ColumnRef;
    zip?: string;
    country?: ColumnRef;
  };
  birthMonth?: string;
  birthday?: string;
  customFields?: Record<string, string>;
}

/**
 * Operator overrides keyed by import field key ("email", "address.city", …):
 * a CSV header that replaces the provider default entirely — the server does not fall back to it — or null to skip.
 */
export type ImportColumnMappings = Record<string, string | null>;

export enum ImportProvider {
  SHOPIFY = "shopify",
  KLAVIYO = "klaviyo",
  ATTENTIVE = "attentive",
  CUSTOM = "custom",
}

/**
 * Hard cap on subscriber-import CSV size, enforced server-side against the
 * uploaded S3 object (see queueCustomerImports) to bound import-processing cost.
 */
export const MAX_IMPORT_FILE_SIZE_BYTES = 500 * 1024 * 1024;

export const importProviderMapping: Record<ImportProvider, ImportMapping> = {
  [ImportProvider.SHOPIFY]: {
    provider: ImportProvider.SHOPIFY,
    shopifyCustomerId: ["Customer ID", "ID"],
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Phone",
    tags: "Tags",
    acceptsEmailMarketing: "Accepts Email Marketing",
    emailMarketingTrueValue: "yes",
    // No *MarketingUnsubscribedValue: Shopify "no" means "not subscribed", not
    // "unsubscribe me" — only genuine opt-out signals may unsubscribe.
    acceptsSmsMarketing: "Accepts SMS Marketing",
    smsMarketingTrueValue: "yes",
    address: {
      street1: "Default Address Address1",
      street2: "Default Address Address2",
      city: "Default Address City",
      state: "Default Address Province Code",
      zip: "Default Address Zip",
      country: ["Default Address Country Code", "Default Address Country"],
    },
  },
  [ImportProvider.KLAVIYO]: {
    provider: ImportProvider.KLAVIYO,
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Phone Number",
    acceptsEmailMarketing: "Email Marketing Consent",
    emailMarketingTrueValue: "SUBSCRIBED",
    emailMarketingUnsubscribedValue: "UNSUBSCRIBED",
    emailMarketingSuppressed: "Email Suppressions",
    acceptsSmsMarketing: "SMS Marketing Consent",
    smsMarketingTrueValue: "SUBSCRIBED",
    smsMarketingUnsubscribedValue: "UNSUBSCRIBED",
    address: {
      street1: "Address",
      street2: "Address 2",
      city: "City",
      state: ["State/Region", "State / Region"],
      zip: "Zip Code",
      country: "Country",
    },
  },
  [ImportProvider.ATTENTIVE]: {
    provider: ImportProvider.ATTENTIVE,
    firstName: "First Name",
    lastName: "Last Name",
    email: "Subscriber Email",
    phone: "Subscriber Phone Number",
    acceptsSmsMarketing: "SMS Marketing Status",
    smsMarketingTrueValue: "ACTIVE",
    smsMarketingOptedOut: "SMS Marketing Opted Out",
    smsMarketingOptedOutTrueValue: "TRUE",
    acceptsEmailMarketing: "Email Marketing Status",
    emailMarketingTrueValue: "ACTIVE",
    emailMarketingOptedOut: "Email Marketing Opted Out",
    emailMarketingOptedOutTrueValue: "TRUE",
  },
  [ImportProvider.CUSTOM]: {
    provider: ImportProvider.CUSTOM,
    email: "Email",
    firstName: "First Name",
    lastName: "Last Name",
    fullName: "Full Name",
    phone: "Phone",
    tags: "Tags",
    acceptsEmailMarketing: "Email Marketing Subscribed",
    emailMarketingTrueValue: "TRUE",
    // No *MarketingUnsubscribedValue: "FALSE" means "not subscribed", not
    // "unsubscribe me" — only genuine opt-out signals may unsubscribe.
    acceptsSmsMarketing: "SMS Marketing Subscribed",
    smsMarketingTrueValue: "TRUE",
    address: {
      street1: "Address",
      street2: "Address 2",
      city: "City",
      state: "State",
      zip: "Zip Code",
      country: "Country",
    },
    birthMonth: "Birth Month",
    birthday: "Birthday",
  },
};

/** Mirrors csv-parse `{ bom: true, trim: true }`: strip a leading U+FEFF, trim. Case preserved. */
export function normalizeCsvHeader(header: string): string {
  return header.replace(/^\uFEFF/, "").trim();
}

/** normalizeCsvHeader + lowercase + treat "_"/"-" as spaces, collapsed to one. */
export function foldCsvHeader(header: string): string {
  return normalizeCsvHeader(header)
    .toLowerCase()
    .replace(/[-_\s]+/g, " ")
    .trim();
}

const SHOPIFY_HINT_HEADERS = new Set([
  "customer id",
  "accepts email marketing",
  "default address address1",
]);
const KLAVIYO_HINT_HEADERS = new Set([
  "phone number",
  "email marketing consent",
  "email suppressions",
]);
const ATTENTIVE_HINT_HEADERS = new Set([
  "subscriber email",
  "subscriber phone number",
  "sms marketing status",
  "eligible for text marketing",
]);

/**
 * Case/whitespace-insensitive only — deliberately NOT foldCsvHeader: a generic
 * snake_case header like "customer_id" must not flip a non-Shopify file to Shopify.
 */
function foldHeaderForDetection(header: string): string {
  return normalizeCsvHeader(header).toLowerCase().replace(/\s+/g, " ");
}

/** Provider hint from headers. Operator may override in the UI. */
export function detectImportProvider(
  headers: readonly string[],
): ImportProvider {
  const folded = new Set(headers.map(foldHeaderForDetection));
  const hasHint = (hints: Set<string>) =>
    [...folded].some((header) => hints.has(header));
  if (hasHint(SHOPIFY_HINT_HEADERS)) {
    return ImportProvider.SHOPIFY;
  } else if (hasHint(KLAVIYO_HINT_HEADERS)) {
    return ImportProvider.KLAVIYO;
  } else if (hasHint(ATTENTIVE_HINT_HEADERS)) {
    return ImportProvider.ATTENTIVE;
  } else {
    return ImportProvider.CUSTOM;
  }
}

export enum ImportHeaderMatch {
  MATCHED = "matched",
  CASE_MISMATCH = "caseMismatch", // present only after fold — read via resolveImportMappingHeaders
  MISSING = "missing",
  MAPPED = "mapped", // operator-chosen header, present in the file
  EXCLUDED = "excluded", // operator chose not to import this field
}

/** Whether the import activity reads a field that resolved this way. */
export function isReadByServer(match: ImportHeaderMatch): boolean {
  return (
    match === ImportHeaderMatch.MATCHED ||
    match === ImportHeaderMatch.CASE_MISMATCH ||
    match === ImportHeaderMatch.MAPPED
  );
}

export interface ImportHeaderResolution {
  field: string;
  label: string;
  expected: string[];
  required: boolean;
  match: ImportHeaderMatch;
  csvHeader?: string;
}

export interface ImportHeaderValidation {
  provider: ImportProvider;
  fields: ImportHeaderResolution[];
  headers: string[];
  unmappedHeaders: string[];
  blocked: boolean;
  blockedReason?: string;
  warnings: string[];
}

interface ImportFieldDef {
  key: string;
  label: string;
  identity?: true;
  read: (mapping: ImportMapping) => ColumnRef | undefined;
  set: (mapping: ImportMapping, ref: string | undefined) => void;
}

type TopLevelColumnKey =
  | "email"
  | "phone"
  | "shopifyCustomerId"
  | "firstName"
  | "lastName"
  | "fullName"
  | "tags"
  | "acceptsEmailMarketing"
  | "emailMarketingSuppressed"
  | "emailMarketingOptedOut"
  | "acceptsSmsMarketing"
  | "smsMarketingOptedOut"
  | "birthMonth"
  | "birthday";
type AddressKey = keyof NonNullable<ImportMapping["address"]>;

// Type check: every ImportMapping column key must appear in TopLevelColumnKey
// (value-config keys excluded), so a new column can't silently skip validation.
// Exported to count as "used" (same idiom as _carriersAreExhaustive).
export const _importColumnKeysAreExhaustive: Exclude<
  keyof ImportMapping,
  | TopLevelColumnKey
  | "provider"
  | "address"
  | "customFields"
  | `${string}TrueValue`
  | `${string}UnsubscribedValue`
> extends never
  ? true
  : never = true;

const plainField = (
  key: TopLevelColumnKey,
  label: string,
  identity?: true,
): ImportFieldDef => ({
  key,
  label,
  ...(identity ? { identity } : {}),
  read: (m) => m[key],
  set: (m, ref) => {
    m[key] = ref;
  },
});

const addressField = (sub: AddressKey, label: string): ImportFieldDef => ({
  key: `address.${sub}`,
  label,
  read: (m) => m.address?.[sub],
  set: (m, ref) => {
    m.address = { ...m.address, [sub]: ref };
  },
});

/** Identity fields first (order falls out of iteration). Excludes value-config keys like `*TrueValue`. */
const IMPORT_FIELDS: readonly ImportFieldDef[] = [
  plainField("email", "Email", true),
  plainField("phone", "Phone", true),
  plainField("shopifyCustomerId", "Shopify Customer ID", true),
  plainField("firstName", "First Name"),
  plainField("lastName", "Last Name"),
  plainField("fullName", "Full Name"),
  plainField("tags", "Tags"),
  plainField("acceptsEmailMarketing", "Email Marketing"),
  plainField("emailMarketingSuppressed", "Email Suppressions"),
  plainField("emailMarketingOptedOut", "Email Opted Out"),
  plainField("acceptsSmsMarketing", "SMS Marketing"),
  plainField("smsMarketingOptedOut", "SMS Opted Out"),
  addressField("street1", "Address"),
  addressField("street2", "Address 2"),
  addressField("city", "City"),
  addressField("state", "State/Region"),
  addressField("zip", "Zip Code"),
  addressField("country", "Country"),
  plainField("birthMonth", "Birth Month"),
  plainField("birthday", "Birthday"),
];

function flattenColumnRef(ref: ColumnRef): string[] {
  return typeof ref === "string" ? [ref] : ref;
}

/** New mapping with overrides applied; unknown keys ignored; never mutates the input. */
export function applyImportColumnMappings(
  mapping: ImportMapping,
  columnMappings: ImportColumnMappings | null | undefined,
): ImportMapping {
  if (!columnMappings) {
    return mapping;
  }
  const applied: ImportMapping = {
    ...mapping,
    address: mapping.address && { ...mapping.address },
  };
  for (const [key, value] of Object.entries(columnMappings)) {
    const fieldDef = IMPORT_FIELDS.find((entry) => entry.key === key);
    if (!fieldDef) {
      continue;
    }
    fieldDef.set(applied, value === null ? undefined : value);
  }
  return applied;
}

const STRING_COLUMN_KEYS: readonly Exclude<
  TopLevelColumnKey,
  "shopifyCustomerId"
>[] = [
  "email",
  "phone",
  "firstName",
  "lastName",
  "fullName",
  "tags",
  "acceptsEmailMarketing",
  "emailMarketingSuppressed",
  "emailMarketingOptedOut",
  "acceptsSmsMarketing",
  "smsMarketingOptedOut",
  "birthMonth",
  "birthday",
];
const ADDRESS_STRING_KEYS: readonly Exclude<AddressKey, "state" | "country">[] =
  ["street1", "street2", "city", "zip"];

/**
 * Every column reference (and custom-field column) re-spelled the way the file has it
 * when the file only carries a case/underscore variant ("email" for "Email"); exact and absent columns are untouched.
 */
export function resolveImportMappingHeaders(
  mapping: ImportMapping,
  headers: readonly string[],
): ImportMapping {
  const present = new Set(headers.map(normalizeCsvHeader));
  const byFold = new Map<string, string>();
  for (const header of present) {
    const folded = foldCsvHeader(header);
    if (!byFold.has(folded)) {
      byFold.set(folded, header);
    }
  }
  const resolveOne = (column: string): string =>
    present.has(column)
      ? column
      : (byFold.get(foldCsvHeader(column)) ?? column);
  const resolveRef = (ref: ColumnRef): ColumnRef =>
    typeof ref === "string" ? resolveOne(ref) : ref.map(resolveOne);

  const resolved: ImportMapping = {
    ...mapping,
    address: mapping.address && { ...mapping.address },
  };
  for (const key of STRING_COLUMN_KEYS) {
    const column = resolved[key];
    if (column !== undefined) {
      resolved[key] = resolveOne(column);
    }
  }
  if (resolved.shopifyCustomerId !== undefined) {
    resolved.shopifyCustomerId = resolveRef(resolved.shopifyCustomerId);
  }
  if (resolved.address) {
    for (const key of ADDRESS_STRING_KEYS) {
      const column = resolved.address[key];
      if (column !== undefined) {
        resolved.address[key] = resolveOne(column);
      }
    }
    if (resolved.address.state !== undefined) {
      resolved.address.state = resolveRef(resolved.address.state);
    }
    if (resolved.address.country !== undefined) {
      resolved.address.country = resolveRef(resolved.address.country);
    }
  }
  if (mapping.customFields) {
    resolved.customFields = Object.fromEntries(
      Object.entries(mapping.customFields).map(([column, field]) => [
        resolveOne(normalizeCsvHeader(column)),
        field,
      ]),
    );
  }
  return resolved;
}

/**
 * `expected` always reflects the provider default, even under an override, so
 * the UI can show hints/labels regardless of what the operator picked.
 */
function resolveField(
  entry: ImportFieldDef,
  mapping: ImportMapping,
  headers: readonly string[],
  columnMappings: ImportColumnMappings | null | undefined,
): ImportHeaderResolution {
  const baseRef = entry.read(mapping);
  const override = columnMappings?.[entry.key];
  const expected = baseRef === undefined ? [] : flattenColumnRef(baseRef);
  const required = entry.identity === true;

  if (override === null) {
    return {
      field: entry.key,
      label: entry.label,
      expected,
      required,
      match: ImportHeaderMatch.EXCLUDED,
      csvHeader: undefined,
    };
  }
  if (override !== undefined) {
    // Mirrors the server: an override REPLACES the ColumnRef, so a header
    // that's gone from the file means nothing is read for this field.
    const normalizedOverride = normalizeCsvHeader(override);
    const present = headers.includes(normalizedOverride);
    return {
      field: entry.key,
      label: entry.label,
      expected,
      required,
      match: present ? ImportHeaderMatch.MAPPED : ImportHeaderMatch.MISSING,
      csvHeader: present ? normalizedOverride : undefined,
    };
  }

  let match = ImportHeaderMatch.MISSING;
  let csvHeader: string | undefined;
  for (const candidate of expected) {
    const exact = headers.find((header) => header === candidate);
    if (exact !== undefined) {
      match = ImportHeaderMatch.MATCHED;
      csvHeader = exact;
      break;
    }
  }
  if (match === ImportHeaderMatch.MISSING) {
    for (const candidate of expected) {
      const foldedCandidate = foldCsvHeader(candidate);
      const near = headers.find(
        (header) => foldCsvHeader(header) === foldedCandidate,
      );
      if (near !== undefined) {
        match = ImportHeaderMatch.CASE_MISMATCH;
        csvHeader = near;
        break;
      }
    }
  }
  return {
    field: entry.key,
    label: entry.label,
    expected,
    required,
    match,
    csvHeader,
  };
}

function isNearMissOf(header: string, expected: readonly string[]): boolean {
  const folded = foldCsvHeader(header);
  return expected.some((candidate) => foldCsvHeader(candidate) === folded);
}

/**
 * Operator picks that can merge or unsubscribe customers when pointed at the
 * wrong column. Near-miss picks (the auto-mapped case variants) are exempt.
 */
function riskyMappingWarnings(
  fields: readonly ImportHeaderResolution[],
  mapping: ImportMapping,
): string[] {
  const warnings: string[] = [];
  const unsubscribeWarning = (
    field: ImportHeaderResolution,
    token: string | undefined,
    channel: "email" | "SMS",
  ) => {
    if (token) {
      warnings.push(
        `"${field.label}" is mapped to "${field.csvHeader}" — rows equal to "${token}" will unsubscribe the customer from ${channel}.`,
      );
    }
  };
  // Mapping the consent column is not enough: its values still have to match
  // the provider's token, or the import silently subscribes nobody.
  const subscribeWarning = (
    field: ImportHeaderResolution,
    token: string | undefined,
    channel: "email" | "SMS",
  ) => {
    if (token) {
      warnings.push(
        `"${field.label}" is mapped to "${field.csvHeader}" — only rows equal to "${token}" subscribe the customer to ${channel}.`,
      );
    }
  };
  for (const field of fields) {
    if (
      field.match !== ImportHeaderMatch.MAPPED ||
      field.csvHeader === undefined ||
      isNearMissOf(field.csvHeader, field.expected)
    ) {
      continue;
    }
    switch (field.field) {
      case "shopifyCustomerId":
        warnings.push(
          `"${field.label}" is mapped to "${field.csvHeader}" — values must be Shopify customer IDs; anything else is stored on the customer and can merge unrelated customers.`,
        );
        break;
      case "emailMarketingOptedOut":
        unsubscribeWarning(
          field,
          mapping.emailMarketingOptedOutTrueValue,
          "email",
        );
        break;
      case "smsMarketingOptedOut":
        unsubscribeWarning(field, mapping.smsMarketingOptedOutTrueValue, "SMS");
        break;
      case "acceptsEmailMarketing":
        subscribeWarning(field, mapping.emailMarketingTrueValue, "email");
        unsubscribeWarning(
          field,
          mapping.emailMarketingUnsubscribedValue,
          "email",
        );
        break;
      case "acceptsSmsMarketing":
        subscribeWarning(field, mapping.smsMarketingTrueValue, "SMS");
        unsubscribeWarning(field, mapping.smsMarketingUnsubscribedValue, "SMS");
        break;
    }
  }
  return warnings;
}

/**
 * Pre-selects the near-miss column for every case-mismatched field, so a file
 * with e.g. "email" or "email_marketing_consent" is importable without a click.
 */
export function defaultImportColumnMappings(
  headers: readonly string[],
  provider: ImportProvider,
): ImportColumnMappings {
  const columnMappings: ImportColumnMappings = {};
  for (const field of validateImportHeaders(headers, provider).fields) {
    if (
      field.match === ImportHeaderMatch.CASE_MISMATCH &&
      field.csvHeader !== undefined
    ) {
      columnMappings[field.field] = field.csvHeader;
    }
  }
  return columnMappings;
}

// Control chars (excluding tab/LF/CR, which the delimiter check owns) or the
// Unicode replacement char — signs of a binary file, not a CSV header row.
const NOT_CSV_CHARS = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F�]/;
const MAX_PLAUSIBLE_HEADER_LENGTH = 200;

export function validateImportHeaders(
  rawHeaders: readonly string[],
  provider: ImportProvider,
  columnMappings?: ImportColumnMappings | null,
): ImportHeaderValidation {
  const headers = rawHeaders.map(normalizeCsvHeader);
  const nonEmpty = headers.filter((header) => header !== "");
  const mapping = importProviderMapping[provider];

  const fields = IMPORT_FIELDS.map((entry) =>
    resolveField(entry, mapping, headers, columnMappings),
  );
  const identityFields = fields.filter((field) => field.required);

  // A header is "claimed" if the server would read it: an operator mapping's
  // header, or — for fields without an override (an override replaces the
  // ColumnRef) — every present candidate, since readCol tries them all. A case
  // near-miss is claimed too so it isn't double-reported next to its row.
  const claimedHeaders = new Set<string>();
  for (const field of fields) {
    if (columnMappings?.[field.field] !== undefined) {
      if (
        field.match === ImportHeaderMatch.MAPPED &&
        field.csvHeader !== undefined
      ) {
        claimedHeaders.add(field.csvHeader);
      }
      continue;
    }
    if (
      field.match === ImportHeaderMatch.CASE_MISMATCH &&
      field.csvHeader !== undefined
    ) {
      claimedHeaders.add(field.csvHeader);
    }
    for (const candidate of field.expected) {
      if (headers.includes(candidate)) {
        claimedHeaders.add(candidate);
      }
    }
  }
  const unmappedHeaders: string[] = [];
  for (const header of nonEmpty) {
    if (!claimedHeaders.has(header) && !unmappedHeaders.includes(header)) {
      unmappedHeaders.push(header);
    }
  }

  const identityMatched = identityFields.some((field) =>
    isReadByServer(field.match),
  );

  let blocked = false;
  let blockedReason: string | undefined;
  if (nonEmpty.length === 0) {
    blocked = true;
    blockedReason = "No header row found.";
  } else if (!identityMatched) {
    blocked = true;
    const sole = nonEmpty.length === 1 ? (nonEmpty[0] ?? "") : undefined;
    if (
      sole !== undefined &&
      (NOT_CSV_CHARS.test(sole) || sole.length > MAX_PLAUSIBLE_HEADER_LENGTH)
    ) {
      blockedReason = "This doesn't look like a CSV file.";
    } else if (sole !== undefined && /[;\t]/.test(sole)) {
      blockedReason =
        "This file looks semicolon- or tab-delimited. Imports only read comma-separated CSVs.";
    } else {
      const identityDefaults = identityFields
        .map((field) => field.expected[0])
        .filter((label): label is string => label !== undefined);
      const identityLabelsJoined = (
        identityDefaults.length > 0
          ? identityDefaults
          : identityFields.map((field) => field.label)
      )
        .map((label) => `"${label}"`)
        .join(" / ");
      blockedReason = `No ${identityLabelsJoined} column found — nothing would be imported.`;
    }
  }

  // csv-parse's `columns: true` keeps only the LAST column for a repeated
  // header name, so flag duplicates regardless of whether the import blocks.
  const warnings: string[] = [];
  const headerOccurrences = new Map<string, number>();
  for (const header of nonEmpty) {
    headerOccurrences.set(header, (headerOccurrences.get(header) ?? 0) + 1);
  }
  for (const [header, count] of headerOccurrences) {
    if (count > 1) {
      warnings.push(
        `Duplicate column "${header}" — only the last one is read.`,
      );
    }
  }

  if (!blocked) {
    const definesConsent =
      mapping.acceptsEmailMarketing !== undefined ||
      mapping.acceptsSmsMarketing !== undefined;
    const consentFields = fields.filter(
      (field) =>
        field.field === "acceptsEmailMarketing" ||
        field.field === "acceptsSmsMarketing",
    );
    const consentMatched = consentFields.some((field) =>
      isReadByServer(field.match),
    );
    if (definesConsent && !consentMatched) {
      const consentLabelsJoined = consentFields
        .map((field) => `"${field.expected[0] ?? ""}"`)
        .join(" / ");
      warnings.push(
        `No ${consentLabelsJoined} column found — customers will be imported but not subscribed to email or SMS marketing.`,
      );
    }
    warnings.push(...riskyMappingWarnings(fields, mapping));
  }

  return {
    provider,
    fields,
    headers: Array.from(new Set(nonEmpty)),
    unmappedHeaders,
    blocked,
    blockedReason,
    warnings,
  };
}

export enum WorkflowError {
  FAILED = "Workflow execution failed",
  CANCELED = "Customer import cancelled",
}

const CustomerImportProgressSchema_ = z.object({
  importId: z.string(),
  segmentName: z.string().optional(),
  error: createEnum(WorkflowError).optional(),
  provider: createEnum(ImportProvider).optional(),
  lastRow: z.number(),
  totalRows: z.number(),
  progress: z.number(),
  totalCustomersImported: z.number(),
  totalCustomersSkipped: z.number(),
  totalEmailSubscribers: z.number(),
  totalSmsSubscribers: z.number(),
  totalFailed: z.number(),
});

export const CustomerImportProgressSchema: z.ZodType<CustomerImportProgress> =
  CustomerImportProgressSchema_;
export interface CustomerImportProgress extends z.infer<
  typeof CustomerImportProgressSchema_
> {}
